import React, { useState } from 'react';
import { DatePicker, Segmented, Select, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { FtueFilter, FtuePlatform, FtueQuery, FtueVersion } from '@/types';
import {
  DAY_FORMAT,
  MIN_DATE,
  hasCompare,
  istToday,
  otherPlatform,
  suggestDates,
  suggestVersion,
  toFtueQuery,
} from './ftueQuery';
import type { CompareMode } from './ftueQuery';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const MODES: Array<{ value: CompareMode; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'version', label: 'Another version' },
  { value: 'dates', label: 'Other dates' },
  { value: 'platform', label: 'Other platform' },
];

const PLATFORMS: Array<{ value: FtuePlatform; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
];

const disabledDate = (d: Dayjs) => d.isBefore(MIN_DATE, 'day') || d.isAfter(istToday(), 'day');

interface DatesProps {
  from: string;
  to: string;
  label: string;
  onChange(from: string, to: string): void;
}

const DateRange: React.FC<DatesProps> = ({ from, to, label, onChange }) => (
  <RangePicker
    aria-label={label}
    allowClear={false}
    value={[dayjs(from), dayjs(to)]}
    disabledDate={disabledDate}
    onChange={(range) => {
      if (range && range[0] && range[1]) {
        onChange(range[0].format(DAY_FORMAT), range[1].format(DAY_FORMAT));
      }
    }}
  />
);

interface VersionProps {
  value: string;
  versions: FtueVersion[];
  label: string;
  onChange(version: string): void;
}

/** "All", then the versions with install counts; keeps the chosen one visible if it isn't listed. */
const VersionSelect: React.FC<VersionProps> = ({ value, versions, label, onChange }) => {
  const options = [
    { value: 'all', label: 'All' },
    ...versions.map((v) => ({
      value: v.version,
      label: `${v.version} (${v.installs.toLocaleString('en-US')} installs)`,
    })),
  ];
  if (!options.some((o) => o.value === value)) options.push({ value, label: value });
  return (
    <Select
      aria-label={label}
      style={{ minWidth: 220 }}
      showSearch
      optionFilterProp="label"
      value={value}
      options={options}
      onChange={onChange}
    />
  );
};

const Field: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <Text type="secondary" style={{ fontSize: 12 }}>
      {title}
    </Text>
    {children}
  </div>
);

interface Props {
  /** The filter in use, primary keys filled in; any compare key puts the bar in that mode. */
  value: FtueQuery;
  versions: FtueVersion[];
  onChange(query: FtueQuery): void;
}

/** Install dates, app version and platform, plus an optional second filter to compare with. */
export const FtueFilters: React.FC<Props> = ({ value, versions, onChange }) => {
  const primary: FtueFilter = {
    from: value.from ?? MIN_DATE,
    to: value.to ?? istToday(),
    version: value.version ?? 'all',
    platform: value.platform ?? 'all',
  };
  const [compareMode, setCompareMode] = useState<CompareMode>(() => {
    if (value.compareVersion) return 'version';
    if (value.compareFrom || value.compareTo) return 'dates';
    if (value.comparePlatform) return 'platform';
    return 'none';
  });
  const [compare, setCompare] = useState<FtueFilter>(() => ({
    from: value.compareFrom ?? primary.from,
    to: value.compareTo ?? primary.to,
    version: value.compareVersion ?? primary.version,
    platform: value.comparePlatform ?? primary.platform,
  }));

  const emit = (p: FtueFilter, mode: CompareMode, c: FtueFilter) =>
    onChange(toFtueQuery({ primary: p, compareMode: mode, compare: c }));

  const changePrimary = (patch: Partial<FtueFilter>) =>
    emit({ ...primary, ...patch }, compareMode, compare);

  const changeCompare = (patch: Partial<FtueFilter>) => {
    const next = { ...compare, ...patch };
    setCompare(next);
    emit(primary, compareMode, next);
  };

  const changeMode = (mode: CompareMode) => {
    const next = { ...compare };
    if (mode === 'version') next.version = suggestVersion(primary, versions);
    if (mode === 'dates') Object.assign(next, suggestDates(primary) ?? primary);
    if (mode === 'platform') next.platform = otherPlatform(primary.platform);
    setCompareMode(mode);
    setCompare(next);
    emit(primary, mode, next);
  };

  const sameAsMain =
    compareMode !== 'none' && !hasCompare(toFtueQuery({ primary, compareMode, compare }));

  return (
    <Space size={[24, 12]} wrap align="end">
      <Field title="Install dates">
        <DateRange
          label="Install dates"
          from={primary.from}
          to={primary.to}
          onChange={(from, to) => changePrimary({ from, to })}
        />
      </Field>
      <Field title="App version">
        <VersionSelect
          label="App version"
          value={primary.version}
          versions={versions}
          onChange={(version) => changePrimary({ version })}
        />
      </Field>
      <Field title="Platform">
        <Segmented
          options={PLATFORMS}
          value={primary.platform}
          onChange={(platform) => changePrimary({ platform })}
        />
      </Field>
      <Field title="Compare with">
        <Segmented options={MODES} value={compareMode} onChange={(mode) => changeMode(mode)} />
      </Field>
      {compareMode === 'version' && (
        <Field title="Compare version">
          <VersionSelect
            label="Compare version"
            value={compare.version}
            versions={versions}
            onChange={(version) => changeCompare({ version })}
          />
        </Field>
      )}
      {compareMode === 'dates' && (
        <Field title="Compare install dates">
          <DateRange
            label="Compare install dates"
            from={compare.from}
            to={compare.to}
            onChange={(from, to) => changeCompare({ from, to })}
          />
        </Field>
      )}
      {compareMode === 'platform' && (
        <Field title="Compare platform">
          <Segmented
            options={PLATFORMS}
            value={compare.platform}
            onChange={(platform) => changeCompare({ platform })}
          />
        </Field>
      )}
      {sameAsMain && (
        <Text type="warning" style={{ fontSize: 13 }}>
          The compare filter is the same as the main one, so only one column shows. Pick a different
          one.
        </Text>
      )}
    </Space>
  );
};
